import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Avatar, backLinkClasses, chooseTheme, cn, Icon } from '@evogard/ui';
import { THEMES, formatInstant } from '@evogard/domain';
import type { Theme } from '@evogard/domain';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '@evogard/ui/primitives';
import type { MenuModule } from '../menu.js';
import { BalanceWidget } from './BalanceWidget.js';
import { BreakOverrunsBadge } from './BreakOverrunsBadge.js';
import { BreakToggle } from './BreakToggle.js';
import { can, useSavePreferences, useSessionContext } from '../lib/session.js';
import {
  notificationLink,
  useMarkNotificationsRead,
  useMyNotifications,
} from '../lib/notifications.js';

/**
 * A faixa do topo da interna — a trilha à esquerda, os controles à direita.
 *
 * ## Por que ela virou um arquivo
 *
 * Ela morava dentro do `Layout`, e com ela as quatro peças que só existem aqui:
 * a trilha, o sino, o menu da conta e a gaveta do telefone. O `Layout` ficava
 * com 961 linhas respondendo por duas coisas de naturezas diferentes — o GRID
 * da moldura e o conteúdo de uma das faixas dele. Separar não muda um pixel:
 * muda onde se procura quando o topo está errado.
 *
 * ## O desenho de 16/09, e o que dele NÃO entrou
 *
 * A referência tem 60px de altura, respiro de 28px à esquerda e 16px à direita,
 * a trilha em 13px com o último degrau em laranja, e os controles em caixas de
 * 34px com raio de 10px. Tudo isso entrou.
 *
 * Três coisas dela ficaram de fora, e as três pela mesma regra do `CLAUDE.md` —
 * nenhuma tela afirma o que o código não cumpre:
 *
 * - **o botão "Novo"**, que abriria um menu de criação. Não existe lista de
 *   ações de criação por módulo em `menu.ts`, e um botão que abre um menu vazio
 *   — ou, pior, com destinos inventados — é pior do que a ausência dele;
 * - **os três ícones ao lado do sino** (ajuda, mapa, IA). Nenhum tem destino no
 *   produto; seriam três botões decorativos com aparência de ação;
 * - **o título da página dentro da faixa.** Ele é o `<h1>` de cada tela, e quem
 *   o desenha é o `PageHeader` (`packages/ui/src/Page.tsx`), junto da descrição
 *   e das ações da página. Trazê-lo para cá sem elas deixaria o mesmo nome
 *   escrito duas vezes na mesma tela; trazê-lo COM elas não cabe em 60px.
 *
 * A altura é `h-15` e não `h-14`: os 60px da referência. Em ponteiro grosso os
 * controles crescem para 44px, que é o piso do `DESIGN.md` — a caixa de 34px é
 * a do mouse, e as duas convivem no mesmo elemento pelo `lg:`.
 *
 * ## Os cantos e a sombra
 *
 * A faixa perdeu o `border-b` e ganhou `shadow-faixa` — a sombra dirigida para
 * baixo, declarada em `packages/ui/src/theme.css`. Linha de 1px E sombra é a
 * mesma separação contada duas vezes, e sobre canto arredondado a linha corta a
 * curva ao meio.
 *
 * `rounded-b-grande` no telefone e `lg:rounded-grande` na área de trabalho, e a
 * diferença não é gosto: no telefone a faixa encosta nas três bordas da janela,
 * e só o lado de baixo tem canto para arredondar — que é exatamente o que a
 * referência mostra. Acima de `lg` o grid dá 8px dos quatro lados (`lg:p-2`) e
 * ela vira um painel solto, como a lateral ao lado: um painel com dois cantos
 * quadrados e fundo em volta lê como cartão cortado.
 *
 * ## Ela é `sticky` no telefone e `lg:static` na área de trabalho
 *
 * Acima de `lg` a moldura é a janela e não rola (`Layout`): quem rola é o
 * `<main>` abaixo daqui. A faixa então não precisa de `sticky` nenhuma — ela
 * fica no alto da coluna, encostada na lateral e na mesa, e é a rolagem do
 * conteúdo que passa por baixo dela.
 *
 * Era `sticky top-0` nos dois tamanhos, e na área de trabalho isso a colava no
 * alto da janela ao rolar, comendo os 8px de respiro: a faixa se mexia junto
 * com a rolagem em vez de ficar onde nasceu. Medido em 17/09/2026.
 *
 * `sticky top-0` continua **no telefone**, onde quem rola é o documento. Ali
 * `top-0` e não um valor com fresta: com fresta, a tela rolando por trás
 * apareceria nela como uma linha de texto em movimento no alto da janela.
 *
 * `shrink-0` porque a coluna é um flex de altura fixa acima de `lg` — sem ele
 * a faixa de 60px é espremida quando a mesa tem conteúdo alto.
 *
 * **A sombra e os cantos saíram em 07/10, e quem separa a faixa agora é uma
 * linha.** Ela era `shadow-faixa` nas duas larguras, com `rounded-b-grande` no
 * telefone e `lg:rounded-grande` na área de trabalho — os três fazem sentido
 * para uma peça que FLUTUA, e a moldura deixou de flutuar (ver o docblock da
 * lateral em `Layout.tsx`). No lugar vem `border-b border-borda`: 1px, a mesma
 * linha que separa a lateral da mesa.
 *
 * No telefone a linha faz o trabalho que a sombra fazia — a tela ainda passa
 * por baixo, e o que a detém é a borda, não o degrau.
 */
export function Header({
  tenant,
  module,
  page,
  path,
  user,
  theme,
  notificationSound,
  sidebar,
  toSignOut,
  sidebarCollapsed = false,
  onToggleSidebar,
}: {
  tenant: string;
  module: MenuModule | null;
  /** O rótulo do item aberto — o último degrau da trilha. */
  page: string | null;
  /** O endereço atual. Fecha a gaveta ao navegar, e tira o link do degrau aberto. */
  path: string;
  user: { nome: string; email: string };
  theme: Theme;
  /** Se o aviso em tempo real toca `notification.mp3`. */
  notificationSound: boolean;
  /** A lateral, para dentro da gaveta do telefone. Quem a monta é o `Layout`. */
  sidebar: ReactNode;
  /** A lateral da área de trabalho está a 64px. Estado do `Layout`. */
  sidebarCollapsed?: boolean;
  /** Recolhe e estende a lateral. Ausente abaixo de `lg`, onde há a gaveta. */
  onToggleSidebar?: () => void;
  toSignOut: () => Promise<void>;
}) {
  const [drawer, setDrawer] = useState(false);

  /*
   * A gaveta fecha ao navegar. Sem isto ela fica aberta sobre a tela nova, e a
   * pessoa toca duas vezes para ver o que pediu.
   */
  useEffect(() => setDrawer(false), [path]);

  return (
    <header
      data-print="fora"
      className="sticky top-0 z-20 flex h-15 shrink-0 items-center gap-4 border-b border-borda bg-superficie px-4 lg:static lg:pl-7 lg:pr-4"
    >
      <Sheet open={drawer} onOpenChange={setDrawer}>
        <SheetTrigger
          aria-label="Abrir o menu"
          className={cn(ICON_BUTTON, '-ml-2 lg:hidden')}
        >
          <Icon nome="menu" size={20} />
        </SheetTrigger>

        <SheetContent side="left" className="w-[17rem] p-0">
          {/*
           * O Radix exige um título; ele é lido em voz alta ao abrir e não tem
           * lugar no desenho, então vai só para o leitor de tela.
           */}
          <SheetTitle className="sr-only">Menu de navegação</SheetTitle>
          {sidebar}
        </SheetContent>
      </Sheet>

      {/*
       * O botão que RECOLHE a lateral, e só acima de `lg`.
       *
       * Abaixo disso o vizinho à esquerda é o gatilho da gaveta, que abre a
       * lateral inteira — dois controles para a mesma coluna na mesma faixa
       * seriam duas respostas para "onde fica o menu". Eles nunca aparecem
       * juntos: um é `lg:hidden`, o outro é `max-lg:hidden`.
       *
       * `aria-expanded` porque o botão controla uma região que continua na
       * tela e muda de tamanho — não é um `aria-pressed`, que diria que ele é
       * um interruptor de si mesmo.
       */}
      {onToggleSidebar && (
        <>
          <button
            type="button"
            onClick={onToggleSidebar}
            aria-expanded={!sidebarCollapsed}
            aria-controls="navegacao-principal"
            aria-label={sidebarCollapsed ? 'Estender o menu' : 'Recolher o menu'}
            title={sidebarCollapsed ? 'Estender o menu' : 'Recolher o menu'}
            className={cn(ICON_BUTTON, 'max-lg:hidden')}
          >
            <Icon nome="menu" size={20} />
          </button>
          {/* o traço do desenho, que separa o controle da coluna da trilha */}
          <span aria-hidden className="h-6 w-px shrink-0 bg-borda max-lg:hidden" />
        </>
      )}

      <Breadcrumb tenant={tenant} module={module} page={page} path={path} />

      <span className="flex-1" />

      {/* Os 12px entre os controles, da referência. */}
      <div className="flex shrink-0 items-center gap-3">
        <BreakToggle />
        <BreakOverrunsBadge />
        <BalanceWidget />
        <NotificationsBell />
        <MenuUser
          nome={user.nome}
          email={user.email}
          theme={theme}
          notificationSound={notificationSound}
          toSignOut={toSignOut}
        />
      </div>
    </header>
  );
}

/**
 * A caixa de 34px dos controles do topo — branca, com borda e raio de 10px.
 *
 * Escrita uma vez porque são duas aparições hoje (a gaveta e o sino) e porque o
 * defeito de copiá-la é conhecido: a régua do design nasceu de nove dialetos de
 * `<h1>`, e nove dialetos começam como duas cópias iguais.
 *
 * `rounded-borda` são 9px e a referência pede 10px. O token é o que existe, e
 * um pixel de raio não vale um token novo: ter dois raios quase iguais na mesma
 * tela custa mais do que errar um pixel contra o desenho.
 *
 * `size-11` em ponteiro grosso, `lg:size-[2.125rem]` no mouse: 44px onde há
 * dedo é o piso do `DESIGN.md`, e 34px é a medida da referência.
 */
const ICON_BUTTON =
  'relative flex size-11 shrink-0 items-center justify-center rounded-borda border border-borda bg-superficie text-texto-suave transition-colors hover:bg-superficie-suave hover:text-texto data-[state=open]:bg-superficie-suave lg:size-[2.125rem]';

/**
 * A trilha do topo: base / módulo / tela.
 *
 * Ela responde "onde eu estou" agora que a lateral mostra tudo ao mesmo tempo.
 * Com o rail, o módulo aberto era a única coisa acesa e a pergunta se
 * respondia sozinha; com sete grupos abertos, não.
 *
 * ## O degrau do MÓDULO virou link em 18/09, e os outros dois não
 *
 * Ela não navegava — nenhum degrau era link, e este cabeçalho declarava a
 * ausência como desenho. Visto no navegador: em
 * "Evogard Ceará › Demandas › Relatório" não há como voltar para a mesa de
 * demandas pelo caminho que a própria trilha desenha. Trilha é a forma que o
 * produto usa para dizer "você desceu por aqui" — sem o clique, ela promete um
 * caminho e entrega um rótulo.
 *
 * **O módulo leva ao `start` dele**, que é exatamente onde o clique no módulo
 * da lateral já levava: não é um caminho novo, é o mesmo com outra porta. E é
 * um destino que a pessoa PODE abrir — `visibleModules` reescreve o `start` de
 * quem não alcança o primeiro item, então o link nunca oferece o que o guard
 * fecha.
 *
 * Os outros dois continuam texto, cada um por um motivo:
 *
 * - **a base não é um lugar.** "Evogard Ceará" não tem tela; ela se troca na
 *   lateral, e um link daqui teria de inventar para onde ir. O ícone de casa à
 *   frente é `aria-hidden` pelo mesmo motivo: ele ancora a linha, não promete
 *   um clique;
 * - **o último degrau é onde a pessoa já está.** Link para a página aberta é
 *   clique que não faz nada.
 *
 * Pela mesma régua, o degrau do módulo **também vira texto quando o `start`
 * dele é o endereço aberto** — é o caso de "ERP › Saúde da integração", em que
 * a primeira tela do módulo é a que está na frente.
 *
 * ## O que ela ainda não sabe dizer
 *
 * **Numa tela de detalhe não há degrau para ela.** Em `/tickets/123` o item
 * aceso é `/tickets`, cujo rótulo é o do próprio módulo, e a dedução abaixo
 * apaga o repetido: a trilha fica "Evogard Ceará › Demandas" e a demanda aberta
 * não aparece — nem há como voltar para a lista por aqui, que é justamente onde
 * voltar mais vale. Dar-lhe um degrau exige o título da tela chegar até este
 * arquivo, e hoje quem o desenha é o `PageHeader` de cada rota.
 *
 * ## O elemento
 *
 * `<nav>` com `<ol>`, e não o `<p>` de antes: com um link dentro, a trilha
 * passou a ser navegação de fato, e é isso que um leitor de tela precisa ouvir.
 * O rótulo continua "Onde você está" — é por ele que o e2e a encontra.
 *
 * O último degrau é laranja desde 16/09 — o `activeColor` da referência —, e
 * não mais escuro em peso 600. É a única coisa da faixa com a cor da marca, e é
 * o que o olho procura ao chegar numa tela nova.
 */
function Breadcrumb({
  tenant,
  module,
  page,
  path,
}: {
  tenant: string;
  module: MenuModule | null;
  page: string | null;
  path: string;
}) {
  /*
   * O degrau repetido sai.
   *
   * Em Início o módulo e a tela têm o mesmo nome, e a trilha saía
   * "Evogard Ceará / Início / Início" — visto no navegador em 06/09. Vale para
   * todo módulo de uma tela só, que é a forma que a regra 2 do `menu.ts`
   * permite e que este produto já tem.
   */
  const steps = [
    { label: tenant, to: null },
    module && { label: module.label, to: module.start },
    page && page !== module?.label ? { label: page, to: null } : null,
  ].filter((d): d is { label: string; to: string | null } => Boolean(d));

  return (
    <nav
      aria-label="Onde você está"
      className="flex min-w-0 items-center gap-1.5 text-[0.8125rem] text-texto-tenue"
    >
      {/*
       * 16px e não os 14px da referência: a escala do `Icon` tem três degraus
       * (16/20/24) e o tipo recusa o resto. É de propósito — um ícone por
       * tamanho de texto é como a interna chegou a nove dialetos de título.
       */}
      <Icon nome="inicio" size={16} className="shrink-0" />
      <ol className="flex min-w-0 items-center gap-1.5">
        {steps.map((step, i) => {
          const isLast = i === steps.length - 1;
          /*
           * Só vira link o degrau que leva para OUTRO lugar: nem o último, que
           * é a tela aberta, nem o módulo cujo `start` é o endereço atual.
           */
          const to = isLast || step.to === null || step.to === path ? null : step.to;
          return (
            /*
             * O degrau inteiro some no telefone, separador junto — e isso é
             * conserto, não desenho. O `max-sm:hidden` morava só no rótulo, então
             * a 390px a trilha saía como "/ / Categorias": dois separadores
             * órfãos de degraus que não estavam ali. Nada pegava — o e2e do menu
             * mede o TEXTO da trilha, e o texto continuava certo.
             */
            <li
              key={step.label}
              className={cn(
                'flex min-w-0 items-center gap-1.5',
                !isLast && 'max-sm:hidden',
              )}
            >
              <span aria-hidden className={cn('opacity-60', isLast && 'max-sm:hidden')}>
                ›
              </span>
              {to ? (
                /*
                 * `exact`: sem ele o TanStack considera `/tickets` aceso em
                 * `/tickets/report` e acrescenta `aria-current="page"` sozinho —
                 * seriam dois "página atual" na mesma tela, um deles num link
                 * que leva para outra.
                 *
                 * `backLinkClasses` e não classes à mão: é ele que carrega o
                 * alvo de 44px em ponteiro grosso, que é a régua do `DESIGN.md`
                 * para um link de texto de 13px. O tamanho e a cor da trilha
                 * entram por cima.
                 */
                <Link
                  to={to}
                  activeOptions={{ exact: true }}
                  className={backLinkClasses('min-w-0 text-[0.8125rem] text-texto-tenue')}
                >
                  <span className="truncate">{step.label}</span>
                </Link>
              ) : (
                <span className={cn('truncate', isLast && 'font-medium text-primaria-texto')}>
                  {step.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/**
 * O sino — histórico de notificações, sem ação nenhuma na linha.
 *
 * "Sem ação" é literal para o aviso sem destino: a linha é `<li>` com texto,
 * não `DropdownMenuItem`. Um item de menu é focável, reage a Enter e FECHA o
 * menu ao selecionar — a própria camada estaria prometendo um clique que não
 * faz nada (`CLAUDE.md`: nenhuma tela afirma o que o código não cumpre).
 *
 * **O aviso que tem destino vira item, desde 24/09.** `notificationLink` diz
 * para onde ele leva — hoje, o do pagamento da premiação abre o cartão no
 * quadro do consultor. Aí o item é honesto: Enter navega e fecha o menu.
 *
 * Marca como lida ao ABRIR (`onOpenChange`), não ao listar — `listMine` no
 * back-end nunca marca sozinho, pelo mesmo motivo: a resposta se apagaria
 * antes de a bolinha aparecer.
 *
 * ## O contador, desde 16/09
 *
 * Era uma bolinha vermelha de 8px, que dizia "há algo" e mais nada. A
 * referência põe ali o NÚMERO, num disco navy — e o número já existia: é o
 * mesmo `unread` que o `aria-label` lê em voz alta desde sempre. A bolinha era
 * a interface sabendo menos do que o código.
 *
 * Passando de nove ele vira `9+`. Dois dígitos ainda cabem no disco de 18px;
 * três o esticariam num losango, e a diferença entre 47 e 112 não muda o que a
 * pessoa faz a seguir — as duas querem dizer "abra e veja".
 */
function NotificationsBell() {
  const notifications = useMyNotifications();
  const { data: context } = useSessionContext();
  const navigate = useNavigate();
  const markRead = useMarkNotificationsRead();
  const items = notifications.data ?? [];
  const unread = items.filter((n) => n.readAt === null).length;

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open && unread > 0) markRead.mutate();
      }}
    >
      <DropdownMenuTrigger
        aria-label={unread > 0 ? `Notificações — ${unread} não lida(s)` : 'Notificações'}
        className={ICON_BUTTON}
      >
        <Icon nome="sino" size={20} />
        {unread > 0 && (
          /*
           * `-top-1 -right-1` são os 4px para fora da caixa, da referência. O
           * disco é `chrome-alto` e não `chrome`: é a mesma cor da pastilha da
           * conta ao lado, e o par `chrome-texto sobre chrome-alto` já é cobrado
           * em `theme.test.ts`. Um navy novo só para este disco abriria a
           * terceira cor escura da faixa sem nada que a medisse.
           */
          <span
            aria-hidden
            className="absolute -right-1 -top-1 grid size-[1.125rem] place-items-center rounded-pilula bg-chrome-alto text-[0.625rem] font-semibold text-chrome-texto"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" collisionPadding={12} className="min-w-72">
        <DropdownMenuLabel className="font-normal">Notificações</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.length === 0 ? (
          <p className="px-2 py-3 text-sm text-texto-suave">Nenhuma notificação ainda.</p>
        ) : (
          <ul className="flex max-h-80 flex-col gap-0.5 overflow-y-auto">
            {items.map((n) => {
              const link = notificationLink(n, (resource, action) =>
                can(context, resource, action),
              );
              const body = (
                <>
                  <span
                    className={cn(
                      'text-sm',
                      n.readAt === null ? 'font-semibold text-texto' : 'text-texto-suave',
                    )}
                  >
                    {n.subject}
                  </span>
                  <span className="text-xs text-texto-tenue">
                    {formatInstant(n.createdAt)}
                  </span>
                </>
              );
              return link === null ? (
                <li key={n.id} className="flex flex-col gap-0.5 rounded-pequeno px-2 py-2">
                  {body}
                </li>
              ) : (
                <li key={n.id}>
                  <DropdownMenuItem
                    onSelect={() => void navigate({ to: link.to, search: link.search })}
                    className="flex flex-col items-start gap-0.5 py-2 pointer-coarse:py-3"
                  >
                    {body}
                  </DropdownMenuItem>
                </li>
              );
            })}
          </ul>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * A conta, no canto do topo.
 *
 * É o primeiro primitivo em uso na plataforma, e está aqui pelo que ele traz
 * de graça: `Escape` fecha, o foco volta para o gatilho, as setas andam pelos
 * itens, o painel não vaza da janela e o `aria-expanded` conta a história para
 * quem usa leitor de tela. Escrito à mão, seria a semana inteira para chegar
 * pior. Ver `packages/ui/src/primitives/index.ts`.
 *
 * ## O gatilho é a pastilha, e nada mais, desde 16/09
 *
 * Ele era uma caixa com borda, a pastilha dentro e um chevron ao lado. Na
 * referência é o disco sozinho, de 38px. O chevron saiu porque a borda saiu
 * junto: sem caixa em volta, uma seta ao lado de um disco lê como duas peças e
 * não como um controle.
 *
 * O nome acessível passou a viver no `aria-label` do gatilho. Ele morava no
 * `label` do chevron — quando o chevron saiu, o botão ficaria mudo, e botão
 * mudo é a palavra "botão" anunciada sem mais nada para quem navega por leitor.
 *
 * ## E a pastilha é o `Avatar`, não um `<span>`
 *
 * Havia um desenhado à mão aqui, com o próprio tamanho e o próprio raio. É a
 * lição do `Wordmark`: a peça que menos pode divergir é a que identifica, e
 * escrita à mão ela nasce em quatro tamanhos. O círculo de 38px é a medida
 * desta aparição e vai pelo `className`; a cor e as iniciais são do componente,
 * e continuam sendo as mesmas da pastilha no rodapé da lateral.
 */
// @valor
const LABEL_THEME: Record<Theme, string> = {
  sistema: 'Do sistema',
  claro: 'Claro',
  escuro: 'Escuro',
};

function MenuUser({
  nome,
  email,
  theme,
  notificationSound,
  toSignOut,
}: {
  nome: string;
  email: string;
  theme: Theme;
  notificationSound: boolean;
  toSignOut: () => Promise<void>;
}) {
  const save = useSavePreferences();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={`Sua conta — ${nome}`}
        className="shrink-0 rounded-pilula transition-opacity hover:opacity-85 data-[state=open]:opacity-85"
      >
        <Avatar
          nome={nome}
          className="size-11 rounded-pilula text-sm lg:size-[2.375rem]"
        />
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" collisionPadding={12} className="min-w-56">
        <DropdownMenuLabel className="font-normal">
          <span className="block truncate text-sm font-medium">{nome}</span>
          {/* O e-mail é o que identifica a conta quando o nome se repete. */}
          <span className="block truncate text-xs font-normal text-texto-suave">
            {email}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/*
         * O tema aqui, e não só na tela de preferências.
         *
         * O botão da lateral alterna claro e escuro num clique; este é o lugar
         * onde `sistema` continua escolhível. A gravação é a MESMA mutação nos
         * três (`useSavePreferences`), e por isso eles nunca discordam do que
         * foi salvo.
         *
         * `RadioGroup` e não três itens soltos: o estado atual precisa estar
         * visível antes do clique, e é o primitivo que dá isso de graça para
         * quem lê por leitor de tela (`aria-checked`).
         */}
        <DropdownMenuLabel className="text-xs font-normal text-texto-suave">
          Tema
        </DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={theme}
          onValueChange={(value) => {
            const fresh = value as Theme;
            chooseTheme(fresh);
            // a reversão fica em quem pintou antes da resposta — igual à tela
            save.mutate({ theme: fresh }, { onError: () => chooseTheme(theme) });
          }}
        >
          {THEMES.map((t) => (
            <DropdownMenuRadioItem key={t} value={t} className="pointer-coarse:py-3">
              {LABEL_THEME[t]}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>

        <DropdownMenuSeparator />
        {/*
         * O som do aviso em tempo real (`lib/realtime.tsx`). `CheckboxItem`
         * pelo mesmo motivo do `RadioGroup` acima: o estado aparece antes do
         * clique, e o leitor de tela ouve `aria-checked`.
         *
         * `preventDefault` no `onSelect` mantém o menu aberto — é uma chave
         * que se liga e desliga, e fechar a cada toque esconderia o resultado.
         * Sem pintura otimista: o contexto só muda com a resposta do servidor,
         * então o que está marcado é sempre o que foi salvo.
         */}
        <DropdownMenuCheckboxItem
          checked={notificationSound}
          disabled={save.isPending}
          onSelect={(e) => e.preventDefault()}
          onCheckedChange={(checked) => save.mutate({ notificationSound: checked })}
          className="pointer-coarse:py-3"
        >
          Som das notificações
        </DropdownMenuCheckboxItem>

        <DropdownMenuSeparator />
        {/*
         * `pointer-coarse:py-3`: o item do shadcn nasce com 32px, e no telefone
         * este é o único jeito de sair. A régua do DESIGN.md é 44px onde há
         * dedo — 20px de linha + 12px de cada lado. O primitivo não é editado
         * porque o próximo `make ui-primitivo` o reescreve.
         */}
        <DropdownMenuItem
          onSelect={() => void toSignOut()}
          className="pointer-coarse:py-3"
        >
          <Icon nome="sair" size={16} />
          Sair
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
